import reviewsService from "@/services/reviews";
import serviceHandler from "@/helpers/handlers/service";

import moment from "moment";
import { FORMAT } from "@/config/date-format";
import ErrorHandler from "@/helpers/handlers/error";

const findReviewInState = (state, reviewId) =>
  state.data &&
  state.data.results &&
  state.data.results.find(r => r.id === reviewId);

export default {
  async fetchReviews(
    { commit },
    { payload, searchMode = false, showLoading = true, isCurrent = () => true }
  ) {
    if (!payload.only_count && showLoading) {
      commit("SET_LOADING", true);
    }
    try {
      const reviewsData = await serviceHandler(
        searchMode
          ? reviewsService.reviewsSearch(payload)
          : reviewsService.getReviews(payload)
      );
      if (!isCurrent()) return;

      if (!payload.reply && payload.only_count) {
        commit("SET_ALL_REVIEWS_TOTAL_COUNT", reviewsData.count);
        return;
      }
      commit("SET_REVIEWS_DATA", reviewsData);
    } catch (error) {
      if (isCurrent()) ErrorHandler.displayError(error);
    } finally {
      if (showLoading && isCurrent()) {
        commit("SET_LOADING", false);
      }
    }
  },

  // Changing a published reply creates a new review version; the caller swaps it
  // into the list with SET_REPLIED_REVIEW once it no longer needs the old item.
  async postReply(context, payload) {
    return serviceHandler(reviewsService.postReply(payload));
  },
  async postBulkReply({ dispatch }, payload) {
    await serviceHandler(reviewsService.postBulkReply(payload));

    dispatch("handleBulkReply", payload);
  },
  // Deleting a published reply creates a new review version; the caller swaps it
  // into the list with DELETE_REPLIED_REVIEW once it no longer needs the old item.
  async deleteReply(context, payload) {
    return serviceHandler(reviewsService.deleteReply(payload));
  },

  async postNote({ commit }, { reviewId, text }) {
    await serviceHandler(reviewsService.postNote(reviewId, text));
    commit("UPDATE_REVIEW_IN_LIST", {
      id: reviewId,
      patch: { note: { text } }
    });
  },

  async patchNote({ commit }, { reviewId, text }) {
    await serviceHandler(reviewsService.patchNote(reviewId, text));
    commit("UPDATE_REVIEW_IN_LIST", {
      id: reviewId,
      patch: { note: { text } }
    });
  },

  async deleteNote({ commit }, { reviewId }) {
    await serviceHandler(reviewsService.deleteNote(reviewId));
    commit("UPDATE_REVIEW_IN_LIST", {
      id: reviewId,
      patch: { note: null }
    });
  },

  async assignReviewTag({ commit, state }, { reviewId, tag }) {
    await serviceHandler(reviewsService.assignTag(reviewId, tag.id));
    const review = findReviewInState(state, reviewId);
    if (!review) {
      return;
    }
    const existing = review.tags || [];
    const newTags = existing.some(t => t.id === tag.id)
      ? existing.slice()
      : [
          ...existing,
          {
            id: tag.id,
            sentiment: tag.sentiment,
            ...(tag.name && { name: tag.name }),
            is_auto_assigned: false
          }
        ];
    commit("UPDATE_REVIEW_IN_LIST", {
      id: reviewId,
      patch: { tags: newTags }
    });
  },

  async unassignReviewTag({ commit, state }, { reviewId, tagId }) {
    await serviceHandler(reviewsService.unassignTag(reviewId, tagId));
    const review = findReviewInState(state, reviewId);
    if (!review) {
      return;
    }
    const newTags = (review.tags || []).filter(t => t.id !== tagId);
    commit("UPDATE_REVIEW_IN_LIST", {
      id: reviewId,
      patch: { tags: newTags }
    });
  },

  async updateReviewCategory(
    { commit, state },
    { reviewId, tagId, sentiment }
  ) {
    await serviceHandler(
      reviewsService.patchReviewCategory(reviewId, tagId, sentiment)
    );
    const review = findReviewInState(state, reviewId);
    if (!review) {
      return;
    }
    const newTags = (review.tags || []).map(t =>
      t.id === tagId ? { ...t, ...sentiment } : t
    );
    commit("UPDATE_REVIEW_IN_LIST", {
      id: reviewId,
      patch: { tags: newTags }
    });
  },

  handleBulkReply({ commit, state }, { ids, reply }) {
    const reviews = [...state.data.results];

    reviews.forEach(review => {
      if (ids.includes(review.id)) {
        review.reply_comment = reply;
        review.reply_update_time = moment().format(FORMAT.DATE_HMS);
        review.processing_in_progress = true;
      }
    });

    commit("SET_REVIEWS", reviews);
  },
  setSelectedItems({ commit }, payload) {
    commit("SET_SELECTED_ITEMS", payload);
  },
  setAllItemsSelected({ commit }, payload) {
    commit("SET_ALL_ITEMS_SELECTED", payload);
  },
  setIsAiReplyInProgress({ commit }, payload) {
    commit("SET_IS_AI_REPLY_IN_PROGRESS", payload);
  },
  async setAiLimits({ commit }) {
    const aiLimits = await serviceHandler(reviewsService.getAiLimit());
    commit("SET_AI_LIMITS", aiLimits);
  }
};
